import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { usePlaybook, usePlaybookNodes, useCreateNode, useUpdateNode, useDeleteNode, PlaybookNode, NodeOption } from "@/hooks/usePlaybooks";
import { useIsAdmin } from "@/hooks/useAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Plus, 
  Pencil, 
  Trash2, 
  Save, 
  X,
  Loader2, 
  Shield,
  Network,
  Play,
  LayoutGrid,
  GitBranch
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { NodeCanvas } from "@/components/admin/NodeCanvas";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const endTypes = ["success", "warning", "action"];
const optionVariants = ["default", "destructive", "warning"];

interface NodeForm {
  node_id: string;
  question: string;
  description: string;
  is_end: boolean;
  end_type: string;
  tip_text: string;
  options: NodeOption[];
}

const emptyForm: NodeForm = {
  node_id: "",
  question: "",
  description: "",
  is_end: false,
  end_type: "",
  tip_text: "",
  options: [],
};

export default function AdminPlaybookNodes() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: playbook, isLoading: playbookLoading } = usePlaybook(id);
  const { data: nodes, isLoading: nodesLoading } = usePlaybookNodes(id);
  
  const createNode = useCreateNode();
  const updateNode = useUpdateNode();
  const deleteNode = useDeleteNode();

  const [editingNode, setEditingNode] = useState<PlaybookNode | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<NodeForm>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "tree">(() => {
    const saved = localStorage.getItem("playbook-view-mode");
    return (saved as "grid" | "tree") || "tree";
  });

  // Persist view mode
  useEffect(() => {
    localStorage.setItem("playbook-view-mode", viewMode);
  }, [viewMode]);

  const isLoading = adminLoading || playbookLoading || nodesLoading;

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center">
          <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin access.</p>
        </div>
      </AdminLayout>
    );
  }

  if (!playbook) {
    return (
      <AdminLayout>
        <div className="p-8 text-center">
          <p className="text-muted-foreground">Playbook not found</p>
          <Button className="mt-4" onClick={() => navigate("/admin/playbooks")}>
            Back to Playbooks
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const openCreate = () => {
    setForm(emptyForm);
    setIsCreating(true);
  };

  const openEdit = (node: PlaybookNode) => {
    setForm({
      node_id: node.node_id,
      question: node.question,
      description: node.description || "",
      is_end: node.is_end,
      end_type: node.end_type || "",
      tip_text: node.tip_text || "",
      options: node.options || [],
    });
    setEditingNode(node);
  };

  const closeDialog = () => {
    setIsCreating(false);
    setEditingNode(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.node_id || !form.question) {
      toast.error("Node ID and Question are required");
      return;
    }

    try {
      if (editingNode) {
        await updateNode.mutateAsync({
          id: editingNode.id,
          ...form,
          playbook_id: id!,
        });
        toast.success("Node updated");
      } else {
        await createNode.mutateAsync({
          ...form,
          playbook_id: id!,
        });
        toast.success("Node created");
      }
      closeDialog();
    } catch (error: any) {
      toast.error(error.message || "Failed to save node");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteNode.mutateAsync({ id: deleteId, playbookId: id! });
      toast.success("Node deleted");
      setDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete node");
    }
  };

  const addOption = () => {
    setForm({
      ...form,
      options: [...form.options, { label: "", nextNode: "" }],
    });
  };

  const updateOption = (index: number, updates: Partial<NodeOption>) => {
    const newOptions = [...form.options];
    newOptions[index] = { ...newOptions[index], ...updates };
    setForm({ ...form, options: newOptions });
  };

  const removeOption = (index: number) => {
    setForm({
      ...form,
      options: form.options.filter((_, i) => i !== index),
    });
  };

  const getSlug = (title: string) => 
    title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate("/admin/playbooks")} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Playbooks
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
                <Network className="h-6 w-6 text-primary" />
                {playbook.title} - Decision Tree
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage the decision tree nodes and options
              </p>
            </div>
            <div className="flex gap-2">
              <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as "grid" | "tree")}>
                <TabsList>
                  <TabsTrigger value="tree" className="gap-2">
                    <GitBranch className="h-4 w-4" />
                    Tree
                  </TabsTrigger>
                  <TabsTrigger value="grid" className="gap-2">
                    <LayoutGrid className="h-4 w-4" />
                    Grid
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Button variant="outline" onClick={() => navigate(`/app/playbooks/${getSlug(playbook.title)}`)}>
                <Play className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Add Node
              </Button>
            </div>
          </div>
        </div>

        {/* Nodes Display */}
        {nodes?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Network className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No nodes yet. Start by creating the "start" node.</p>
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create Start Node
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === "tree" ? (
          <NodeCanvas
            nodes={nodes || []}
            onEditNode={openEdit}
            onDeleteNode={(id) => setDeleteId(id)}
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {nodes?.map((node) => (
              <Card key={node.id} className={node.node_id === "start" ? "border-primary" : ""}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge variant={node.node_id === "start" ? "default" : "secondary"} className="mb-2">
                        {node.node_id}
                      </Badge>
                      {node.is_end && (
                        <Badge variant="outline" className="ml-2">
                          End: {node.end_type}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(node)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(node.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <CardTitle className="text-base">{node.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  {node.description && (
                    <p className="text-sm text-muted-foreground mb-3">{node.description}</p>
                  )}
                  <div className="space-y-1">
                    {node.options.map((opt, i) => (
                      <div key={i} className="text-sm flex items-center gap-2">
                        <span className="text-muted-foreground">→</span>
                        <span className="truncate">{opt.label}</span>
                        {opt.nextNode && (
                          <Badge variant="outline" className="text-xs flex-shrink-0">
                            {opt.nextNode}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Node Edit Dialog */}
      <Dialog open={isCreating || !!editingNode} onOpenChange={closeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNode ? "Edit Node" : "Create Node"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Node ID</Label>
                <Input
                  value={form.node_id}
                  onChange={(e) => setForm({ ...form, node_id: e.target.value })}
                  placeholder="e.g., start, step1, endSuccess"
                  disabled={!!editingNode}
                />
                <p className="text-xs text-muted-foreground">
                  Use "start" for the entry point
                </p>
              </div>
              <div className="space-y-2 flex items-center gap-4 pt-6">
                <Switch
                  checked={form.is_end}
                  onCheckedChange={(checked) => setForm({ ...form, is_end: checked })}
                />
                <Label>Is End Node</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Question/Title</Label>
              <Input
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="What question should users answer?"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Additional context or instructions"
              />
            </div>

            {form.is_end && (
              <div className="space-y-2">
                <Label>End Type</Label>
                <Select
                  value={form.end_type}
                  onValueChange={(value) => setForm({ ...form, end_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {endTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Tip Text (optional)</Label>
              <Textarea
                value={form.tip_text}
                onChange={(e) => setForm({ ...form, tip_text: e.target.value })}
                placeholder="Pro tip shown below the card"
              />
            </div>

            {/* Options */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Options</Label>
                <Button type="button" variant="outline" size="sm" onClick={addOption}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Option
                </Button>
              </div>
              
              {form.options.length === 0 ? (
                <p className="text-sm text-muted-foreground">No options yet</p>
              ) : (
                <div className="space-y-3">
                  {form.options.map((opt, index) => (
                    <Card key={index} className="p-3">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Input
                            value={opt.label}
                            onChange={(e) => updateOption(index, { label: e.target.value })}
                            placeholder="Option label"
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeOption(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Next Node</Label>
                            <Input
                              value={opt.nextNode || ""}
                              onChange={(e) => updateOption(index, { nextNode: e.target.value })}
                              placeholder="node_id"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Action (optional)</Label>
                            <Input
                              value={opt.action || ""}
                              onChange={(e) => updateOption(index, { action: e.target.value })}
                              placeholder="copy text or call"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Variant</Label>
                            <Select
                              value={opt.variant || "default"}
                              onValueChange={(value) => updateOption(index, { variant: value as any })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {optionVariants.map((v) => (
                                  <SelectItem key={v} value={v}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSave} disabled={createNode.isPending || updateNode.isPending}>
              {(createNode.isPending || updateNode.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              <Save className="mr-2 h-4 w-4" />
              Save Node
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Node?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this node. Other nodes pointing to it will break.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
